import { useEffect, useMemo, useState } from 'react';
import {
    analyticsApi,
    buildAnalyticsQuery,
    type AnalyticsEvent,
    type AnalyticsFilters,
    type AnalyticsUsage,
    type UsageTrendPoint,
} from '../../services/analytics';
import { EventCohorts } from './EventCohorts';
import { EventsByCustomer } from './EventsByCustomer';
import { InactiveAccounts } from './InactiveAccounts';
import { MetricCard } from './MetricCard';
import { RetentionHeatmap } from './RetentionHeatmap';
import { SegmentPanels } from './SegmentPanels';
import { TrendChart, type TrendMetric } from './TrendChart';
import { buildSegmentCards } from './segmentCards';
import { buildUsageCards } from './usageCards';
import styles from './UsagePanel.module.css';

type UsageMetric = 'crfa' | 'dau' | 'wau' | 'mau' | 'ausentes' | 'loginsPorConta30d';

const USAGE_METRICS: TrendMetric<UsageMetric>[] = [
    { key: 'crfa', label: 'CRFA', unit: 'int' },
    { key: 'dau', label: 'DAU', unit: 'int' },
    { key: 'wau', label: 'WAU', unit: 'int' },
    { key: 'mau', label: 'MAU', unit: 'int' },
    { key: 'ausentes', label: 'Ausentes', unit: 'int' },
    { key: 'loginsPorConta30d', label: 'Logins/conta · 30 dias', unit: 'dec' },
];

function trendValue(p: UsageTrendPoint, m: UsageMetric): number | null {
    if (m === 'loginsPorConta30d') return p.loginsPorConta30d;
    return p[m].base > 0 ? p[m].count : null;
}

interface UsagePanelProps {
    filters: AnalyticsFilters;
    /** Falso enquanto o periodo personalizado esta incompleto: nada e consultado. */
    enabled: boolean;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
    onRetry: () => void;
    timeZone: string;
    /** Eventos da tabela Eventos por cliente, vindos de meta. */
    events?: AnalyticsEvent[];
}

interface Settled {
    key: string;
    error: string | null;
}

export function UsagePanel({ filters, enabled, refreshKey, onRetry, timeZone, events }: UsagePanelProps) {
    const [usage, setUsage] = useState<AnalyticsUsage | null>(null);
    const [settled, setSettled] = useState<Settled | null>(null);
    const [metric, setMetric] = useState<UsageMetric>('crfa');

    const requestKey = enabled ? `${buildAnalyticsQuery(filters)}#${refreshKey}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .usage(filters, controller.signal)
            .then((data) => {
                setUsage(data);
                setSettled({ key: requestKey, error: null });
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar o painel.',
                });
            });
        return () => controller.abort();
        // requestKey resume os filtros e o contador de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const error = settled && settled.key === requestKey ? settled.error : null;
    const cards = useMemo(() => (usage ? buildUsageCards(usage.current) : null), [usage]);
    const segments = useMemo(() => (usage ? buildSegmentCards(usage.current) : null), [usage]);

    return (
        <>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>North Star e acesso</h3>
                <p className={styles.sectionSubtitle}>
                    Retenção por conta; DAU, WAU e MAU medem acesso no dia (login ou uso do sistema)
                </p>
            </div>

            {!enabled && <p className={styles.notice}>Informe as duas datas do período personalizado.</p>}

            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={onRetry}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {loading && !usage && (
                <div className={styles.loadingState} aria-live="polite">
                    Carregando...
                </div>
            )}

            {usage && cards && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                    <div className={styles.cardsGrid}>
                        {cards.map((c, i) => (
                            <div key={c.id} className={c.id === 'crfa' ? styles.wide : undefined}>
                                <MetricCard
                                    infoKey={c.id}
                                    title={c.title}
                                    value={c.value}
                                    caption={c.caption}
                                    highlight={c.highlight}
                                    tipAlign={i === 0 ? 'start' : 'end'}
                                />
                            </div>
                        ))}
                    </div>

                    <TrendChart<UsageMetric, UsageTrendPoint>
                        title="Tendência de uso e retenção"
                        infoKey="tendenciaUso"
                        points={usage.trend.points}
                        granularity={usage.trend.granularity}
                        endDay={usage.period.to}
                        metrics={USAGE_METRICS}
                        metric={metric}
                        onMetricChange={setMetric}
                        getValue={trendValue}
                        loading={loading}
                    />

                    {segments && (
                        <div className={styles.split}>
                            <SegmentPanels segments={segments} />
                            <EventCohorts filters={filters} events={events} enabled={enabled} refreshKey={refreshKey} />
                        </div>
                    )}

                    <RetentionHeatmap filters={filters} refreshKey={refreshKey} />

                    <InactiveAccounts filters={filters} enabled={enabled} refreshKey={refreshKey} timeZone={timeZone} />

                    <EventsByCustomer
                        events={events}
                        filters={filters}
                        enabled={enabled}
                        refreshKey={refreshKey}
                        timeZone={timeZone}
                    />
                </div>
            )}
        </>
    );
}
